import type {
  RevenueProps,
} from '@src/Tracking/catalog';
import type {
  TrackingAdapter,
  TrackingEventName,
  TrackingPayload,
} from '@src/Providers/Tracking/TrackingContext';
import {getVendorConsent} from '@src/Providers/DidomiInitProvider/isTrackingEnabled';
import apiConfig from '@src/Configs/apiConfig';

/**
 * AppsFlyer Web SMART Script (PBA) adapter.
 *
 * Loads the AppsFlyer Web PBA script the first time it's needed. After load,
 * events go through window.AF('pba', 'event', { ... }).
 *
 * Only handles attribution + revenue events on web (install attribution
 * doesn't apply — mobile owns that). Per-event routing in the catalog
 * controls which events reach here.
 */

declare global {
  interface Window {
    AF?: (...args: unknown[]) => void;
    AppsFlyerSdkObject?: string;
    AF_SDK_PLUGIN_NAME?: string;
  }
}

let loadPromise: Promise<void> | null = null;

const loadAppsFlyer = (pbaWebId: string): Promise<void> => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (loadPromise) return loadPromise;
  if (typeof window.AF === 'function') return Promise.resolve();

  loadPromise = new Promise((resolve, reject) => {
    try {
      // Standard AppsFlyer Web SMART script bootstrap.
      // Reference: https://dev.appsflyer.com/hc/docs/web-sdk
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (function (t: any, e: string, n: any, s: any, a: any, c: any, i: any) {
        t.AppsFlyerSdkObject = a;
        t.AF =
          t.AF ||
          function (...args: unknown[]) {
            (t.AF.q = t.AF.q || []).push([Date.now()].concat(args as never));
          };
        t.AF.id = t.AF.id || i;
        t.AF.plugins = {};
        c = e.createElement(n);
        c.src = 'https://websdk.appsflyer.com?' + s;
        c.async = 1;
        c.onload = () => resolve();
        c.onerror = () => reject(new Error('AppsFlyer web SDK failed to load'));
        const f = e.getElementsByTagName(n)[0];
        f && f.parentNode && f.parentNode.insertBefore(c, f);
      })(
        window,
        document,
        'script',
        'pba',
        'AF',
        null,
        pbaWebId,
      );
    } catch (err) {
      reject(err as Error);
    }
  });

  return loadPromise;
};

export const createAppsFlyerAdapter = (): TrackingAdapter => ({
  name: 'appsflyer',
  isEnabled: () => {
    if (!apiConfig.tracking.appsFlyerWebEnabled) return false;
    if (!apiConfig.appsFlyerDevKey) return false;
    return getVendorConsent().appsflyer;
  },

  track: async (event: TrackingEventName, props?: TrackingPayload) => {
    if (typeof event !== 'string') return;
    if (!apiConfig.appsFlyerDevKey) return;
    try {
      await loadAppsFlyer(apiConfig.appsFlyerDevKey);
      window.AF?.('pba', 'event', {
        eventType: 'EVENT',
        eventName: event,
        eventValue: props ?? {},
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[appsflyer] track failed', {event, err});
    }
  },

  trackRevenue: async (rev: RevenueProps) => {
    if (!apiConfig.appsFlyerDevKey) return;
    try {
      await loadAppsFlyer(apiConfig.appsFlyerDevKey);
      window.AF?.('pba', 'event', {
        eventType: 'EVENT',
        eventName: 'af_purchase',
        eventValue: {
          af_revenue: rev.price,
          af_currency: rev.currency ?? 'USD',
          af_content_id: rev.product_id,
          af_quantity: rev.quantity ?? 1,
        },
        eventRevenue: rev.price,
        eventRevenueCurrency: rev.currency ?? 'USD',
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[appsflyer] revenue failed', err);
    }
  },
});
